/**
 * Emoji text: spelling words in letter symbols, drawing them as big block letters out of
 * emoji, and swapping or adding emoji after the words they stand for.
 *
 * Everything here is a pure string transform; the widget calls it on each keystroke.
 */

import { EMOJI_WORD_ROWS } from "@/lib/tool-kit/random-fun/emoji-words";

/* ------------------------------------------------------------ letter symbols */

export type LetterStyle = "regional" | "squared" | "negative";
export type LetterGap = "none" | "invisible" | "space";

const CODE_A = 65;
const REGIONAL_A = 0x1_f1_e6;
const SQUARED_A = 0x1_f1_30;
const NEGATIVE_A = 0x1_f1_70;
const KEYCAP = "️⃣";
const TEXT_PRESENTATION = "︎";
const ZERO_WIDTH_SPACE = "​";
/** The four negative squared letters that also exist as emoji; this keeps them as text. */
const EMOJI_CAPABLE_NEGATIVE = new Set(["A", "B", "O", "P"]);
const GAP_TEXT: Readonly<Record<LetterGap, string>> = {
  none: "",
  invisible: ZERO_WIDTH_SPACE,
  space: " ",
};
const ASCII_LETTER = /^[A-Z]$/;
const ASCII_DIGIT = /^[0-9]$/;

/** Other symbols that have an emoji of their own, used in the regional style. */
const SYMBOL_EMOJI: Readonly<Record<string, string>> = {
  "!": "❗",
  "?": "❓",
  "+": "➕",
  "-": "➖",
  $: "\u{1F4B2}",
  "#": `#${KEYCAP}`,
  "*": `*${KEYCAP}`,
};

function letterSymbol(char: string, style: LetterStyle): string | null {
  const upper = char.toUpperCase();
  if (ASCII_LETTER.test(upper)) {
    const index = upper.charCodeAt(0) - CODE_A;
    if (style === "regional") {
      return String.fromCodePoint(REGIONAL_A + index);
    }
    if (style === "squared") {
      return String.fromCodePoint(SQUARED_A + index);
    }
    const base = String.fromCodePoint(NEGATIVE_A + index);
    return EMOJI_CAPABLE_NEGATIVE.has(upper) ? base + TEXT_PRESENTATION : base;
  }
  if (style !== "regional") {
    return null;
  }
  if (ASCII_DIGIT.test(char)) {
    return `${char}${KEYCAP}`;
  }
  return SYMBOL_EMOJI[char] ?? null;
}

export interface SpelledText {
  output: string;
  /** Distinct characters that have no symbol in this style and were left as typed. */
  unchanged: string[];
}

/**
 * Spells `text` in letter symbols. Characters with no symbol (spaces, accents, other
 * scripts) stay as typed. `gap` goes between two converted symbols; the invisible gap
 * (a zero-width space) is what stops neighbouring regional-indicator letters from
 * pairing into a flag.
 */
export function spellLetters(
  text: string,
  style: LetterStyle,
  gap: LetterGap
): SpelledText {
  const separator = GAP_TEXT[gap];
  const unchanged = new Set<string>();
  let output = "";
  let previousConverted = false;
  for (const char of text) {
    const symbol = letterSymbol(char, style);
    if (symbol === null) {
      output += char;
      previousConverted = false;
      if (char.trim() !== "") {
        unchanged.add(char);
      }
      continue;
    }
    output += (previousConverted ? separator : "") + symbol;
    previousConverted = true;
  }
  return { output, unchanged: [...unchanged] };
}

/* -------------------------------------------------------------- block letters */

/** A 5 × 5 pixel font: `#` is a filled square. Space is three empty columns. */
const GLYPHS: Readonly<Record<string, readonly string[]>> = {
  A: [".###.", "#...#", "#####", "#...#", "#...#"],
  B: ["####.", "#...#", "####.", "#...#", "####."],
  C: [".####", "#....", "#....", "#....", ".####"],
  D: ["####.", "#...#", "#...#", "#...#", "####."],
  E: ["#####", "#....", "####.", "#....", "#####"],
  F: ["#####", "#....", "####.", "#....", "#...."],
  G: [".####", "#....", "#..##", "#...#", ".###."],
  H: ["#...#", "#...#", "#####", "#...#", "#...#"],
  I: ["#####", "..#..", "..#..", "..#..", "#####"],
  J: ["..###", "...#.", "...#.", "#..#.", ".##.."],
  K: ["#...#", "#..#.", "###..", "#..#.", "#...#"],
  L: ["#....", "#....", "#....", "#....", "#####"],
  M: ["#...#", "##.##", "#.#.#", "#...#", "#...#"],
  N: ["#...#", "##..#", "#.#.#", "#..##", "#...#"],
  O: [".###.", "#...#", "#...#", "#...#", ".###."],
  P: ["####.", "#...#", "####.", "#....", "#...."],
  Q: [".###.", "#...#", "#.#.#", "#..#.", ".##.#"],
  R: ["####.", "#...#", "####.", "#..#.", "#...#"],
  S: [".####", "#....", ".###.", "....#", "####."],
  T: ["#####", "..#..", "..#..", "..#..", "..#.."],
  U: ["#...#", "#...#", "#...#", "#...#", ".###."],
  V: ["#...#", "#...#", "#...#", ".#.#.", "..#.."],
  W: ["#...#", "#...#", "#.#.#", "##.##", "#...#"],
  X: ["#...#", ".#.#.", "..#..", ".#.#.", "#...#"],
  Y: ["#...#", ".#.#.", "..#..", "..#..", "..#.."],
  Z: ["#####", "...#.", "..#..", ".#...", "#####"],
  "0": [".###.", "#..##", "#.#.#", "##..#", ".###."],
  "1": ["..#..", ".##..", "..#..", "..#..", ".###."],
  "2": [".###.", "#...#", "..##.", ".#...", "#####"],
  "3": ["####.", "....#", ".###.", "....#", "####."],
  "4": ["#..#.", "#..#.", "#####", "...#.", "...#."],
  "5": ["#####", "#....", "####.", "....#", "####."],
  "6": [".###.", "#....", "####.", "#...#", ".###."],
  "7": ["#####", "....#", "...#.", "..#..", "..#.."],
  "8": [".###.", "#...#", ".###.", "#...#", ".###."],
  "9": [".###.", "#...#", ".####", "....#", ".###."],
  "!": ["..#..", "..#..", "..#..", ".....", "..#.."],
  "?": [".###.", "#...#", "..##.", ".....", "..#.."],
  ".": [".....", ".....", ".....", ".....", "..#.."],
  "-": [".....", ".....", ".###.", ".....", "....."],
  ":": [".....", "..#..", ".....", "..#..", "....."],
  " ": ["...", "...", "...", "...", "..."],
};

export const GLYPH_ROWS = 5;
export const MAX_BANNER_CHARACTERS = 120;

export function glyphFor(char: string): readonly string[] | null {
  return GLYPHS[char.toUpperCase()] ?? null;
}

export interface Banner {
  output: string;
  unsupported: string[];
  /** Number of emoji in the output, for the message-length warning. */
  squares: number;
}

function drawLine(chars: readonly string[], on: string, off: string): string[] {
  const rows: string[] = [];
  for (let row = 0; row < GLYPH_ROWS; row += 1) {
    const cells = chars.map((char) =>
      Array.from((GLYPHS[char] as readonly string[])[row] as string, (cell) =>
        cell === "#" ? on : off
      ).join("")
    );
    rows.push(cells.join(off));
  }
  return rows;
}

/**
 * Draws `text` as big letters made of `on` emoji over an `off` background, `perLine`
 * letters to a line. Characters without a glyph are listed and left out; a line break in
 * the text starts a new line.
 */
export function bannerText(
  text: string,
  on: string,
  off: string,
  perLine: number
): Banner {
  const unsupported = new Set<string>();
  const lines: string[][] = [];
  for (const paragraph of text.split("\n")) {
    const chars: string[] = [];
    for (const raw of paragraph.slice(0, MAX_BANNER_CHARACTERS)) {
      const char = raw.toUpperCase();
      if (GLYPHS[char]) {
        chars.push(char);
      } else if (raw.trim() !== "") {
        unsupported.add(raw);
      }
    }
    for (let start = 0; start < chars.length; start += perLine) {
      lines.push(chars.slice(start, start + perLine));
    }
  }
  const blocks = lines.map((line) => drawLine(line, on, off).join("\n"));
  const output = blocks.join("\n\n");
  const squares = output.split(on).length - 1 + (output.split(off).length - 1);
  return { output, unsupported: [...unsupported], squares };
}

/* ------------------------------------------------------------ words to emoji */

export type WordMode = "replace" | "after";

const WORD_TO_EMOJI = new Map<string, string>(
  EMOJI_WORD_ROWS.map((row) => {
    const [word = "", hex = ""] = row.split("|");
    return [
      word,
      String.fromCodePoint(
        ...hex.split(".").map((part) => Number.parseInt(part, 16))
      ),
    ];
  })
);

const WORD_RUN = /[\p{L}\p{N}']+/gu;

/** Every word the tool can turn into an emoji, for the page and the tests. */
export function knownWords(): string[] {
  return [...WORD_TO_EMOJI.keys()];
}

export function emojiForWord(word: string): string | null {
  const lower = word.toLowerCase();
  const direct = WORD_TO_EMOJI.get(lower);
  if (direct) {
    return direct;
  }
  // Plurals: cats → cat, dishes → dish, cherries → cherry. Never shorten a short word.
  if (lower.length > 3 && lower.endsWith("ies")) {
    const singular = WORD_TO_EMOJI.get(`${lower.slice(0, -3)}y`);
    if (singular) {
      return singular;
    }
  }
  if (lower.length > 3 && lower.endsWith("es")) {
    const singular = WORD_TO_EMOJI.get(lower.slice(0, -2));
    if (singular) {
      return singular;
    }
  }
  if (lower.length > 2 && lower.endsWith("s")) {
    return WORD_TO_EMOJI.get(lower.slice(0, -1)) ?? null;
  }
  return null;
}

export interface WordResult {
  output: string;
  /** How many words became (or gained) an emoji. */
  matched: number;
}

/** Replaces each known word with its emoji, or adds the emoji after the word. */
export function wordsToEmoji(text: string, mode: WordMode): WordResult {
  let matched = 0;
  const output = text.replace(WORD_RUN, (word) => {
    const emoji = emojiForWord(word);
    if (emoji === null) {
      return word;
    }
    matched += 1;
    return mode === "replace" ? emoji : `${word} ${emoji}`;
  });
  return { output, matched };
}
